// ---------------------------------------------------------------------------
// Sonda de consistência filamento x candles (só diagnóstico, só com "?dbg=1").
//
// Pergunta: por 1 ou 2 quadros, o filamento 3D é desenhado com geometria, deslocamento ou escala de um
// estado anterior enquanto os candles já estão no estado novo?
//
// Como mede (sem alterar nada do app):
//  - Os grupos de deslocamento/escala recebem seus valores no useFrame, a partir das props "commitadas"
//    do React. Os buffers da GPU (FastLine e velas) só recebem a geometria nova em useEffect (passivo),
//    DEPOIS do commit. Entre os dois há uma janela. A sonda guarda, no momento do upload, o descritor do
//    que foi realmente enviado (dados, margem do lote, domínio de preço, escala Z) e, a cada quadro,
//    compara:
//      Δx: posição X mundial da mesma vela (mesmo carimbo de tempo) pelo caminho do filamento
//          (geometria enviada + deslocamento aplicado) menos pelo caminho dos candles, em larguras de vela;
//      Δy: o mesmo para o preço de referência, em % da altura da parede;
//      Δz: escala Z aplicada ao filamento contra a escala Z que o controlador pretende, em %;
//      piscada: contagem de vértices/visibilidade do filamento inconsistente.
//  - Também conta os quadros em que o que foi commitado difere do que está na GPU ("velho") e mede a
//    janela commit -> upload em ms e em quadros, separada por causa (troca de lote x dados novos).
//  - Repintura do indicador (H8): a cada upload de dados novos do filamento, compara os pontos que JÁ existiam
//    (mesmo carimbo de tempo) com o upload anterior. Uma carga de histórico muda o início da série e o
//    indicador é recalculado; o preço (candles) não muda, mas momentum/trend (filamento) podem mudar de
//    forma. Isso não é atraso de buffer: é o dado que mudou.
// Zero alocação por quadro: só números e referências em campos pré-existentes.
// ---------------------------------------------------------------------------

import { DBG_ENABLED } from './dbgFlag';
import { jank, EV, NEV, EV_NAMES, MODE_NAMES, markEvent } from './jankRecorder';
import { p1, p1LoteDisparo, p1LoteCommit } from './p1Probe';

export const SC = {
  APP_RENDER: 0, SCENE_RENDER: 1, SLICER_RENDER: 2, WAVE_CALL_MAIN: 3, WAVE_CALL_WALL: 4,
  WAVE_REBUILD_MAIN: 5, WAVE_REBUILD_WALL: 6, UPLOAD_FIL: 7, UPLOAD_CAND: 8,
  COMMIT_LOTE: 9, COMMIT_LOTE_PAREDE: 10, COMMIT_DADOS_FIL: 11, COMMIT_DADOS_CAND: 12,
} as const;
export const NC = 13;
export const SC_NAMES = [
  'render do App', 'render da cena', 'render do ChartDataSlicer', 'useWaveGeometry (lote principal): chamadas', 'useWaveGeometry (parede): chamadas',
  'useWaveGeometry (lote principal): reconstruções', 'useWaveGeometry (parede): reconstruções', 'upload do filamento', 'upload dos candles',
  'commit de troca de lote', 'commit de troca de lote (parede)', 'commit de dados novos (filamento)', 'commit de dados novos (candles)',
];

export const SMODE_NAMES = [
  'arrasto manual', 'autoplay 15', 'autoplay 60', 'autoplay 180',
  'parado no presente (auto-rotate ligado)', 'parado no presente (auto-rotate desligado)', 'parado no histórico', 'câmera por gesto (parado)',
];
export const NSM = 8;

/** Modo da sonda a partir do modo de navegação do gravador (0 parado, 1 manual, 2/3/4 autoplay 15/60/180). */
export function statMode(nav: number, pos: number, autoRotateOn: boolean, camMoving: boolean): number {
  if (nav === 1) return 0;
  if (nav >= 2) return nav - 1;
  if (camMoving && !autoRotateOn) return 7;
  if (pos > 50) return 6;
  return autoRotateOn ? 4 : 5;
}

export const F_STALE_F = 1, F_STALE_C = 2, F_PISCADA = 4, F_FORA = 8;
export const TH_DX = 0.25, TH_DY = 0.5;
/** Repintura: momentum (% do maior |momentum| visível) e Y (trend+imf, % da altura do lote). */
export const TH_RP_M = 2, TH_RP_Y = 0.5;
const RP_CAP = 200;

const ALARM_CAP = 1000;
const SEQ_CAP = 64;
const SEQ_STRIDE = 10;
const f = (x: number, d = 1) => (isFinite(x) ? x.toFixed(d) : '—');

export class SyncProbe {
  // contadores por modo
  readonly counts = new Float64Array(NSM * NC);
  readonly tot = new Float64Array(NC);
  private lastTot = new Float64Array(NC);
  curMode = 4;
  readonly modeFrames = new Float64Array(NSM);
  readonly modeTime = new Float64Array(NSM);
  readonly staleF = new Float64Array(NSM);
  readonly staleC = new Float64Array(NSM);
  /** Quadros com commit pendente: o React já commitou lote/dados novos que ainda não estão na GPU (o desenho segura o lote antigo). */
  readonly pendF = new Float64Array(NSM);
  readonly pendC = new Float64Array(NSM);
  pendNowF = false; pendNowC = false;
  readonly alarmDx = new Float64Array(NSM);
  readonly alarmDy = new Float64Array(NSM);
  readonly alarmBlink = new Float64Array(NSM);
  readonly alarmOut = new Float64Array(NSM);
  readonly alarmAny = new Float64Array(NSM);
  readonly causeCounts = new Float64Array(NEV);
  alarmNoCause = 0;
  maxAbsDx = 0; maxAbsDy = 0;

  // descritores do que está na GPU (preenchidos no upload)
  uDataF: ArrayLike<any> | null = null; uPadF = 0; uMinF = 0; uMaxF = 1; uZsF = 1; uCntF = 0; uSliceF = 0;
  uDataC: ArrayLike<any> | null = null; uPadC = 0; uMinC = 0; uMaxC = 1; uSliceC = 0;

  // entradas por quadro (a cena preenche antes de chamar frame())
  cDataF: ArrayLike<any> | null = null; cDataC: ArrayLike<any> | null = null; cSliceF = 0; cSliceC = 0;
  stepX = 1; H = 1200; vis = 1000;
  gxF = 0; gxC = 0; yAF = 1; yCF = 0; yAC = 1; yCC = 0; zKF = 1; zsExp = NaN;
  drawCount = 0; drawVisible = true; pos = 0; autoRotateOn = false; camMoving = false; verF = 0; verC = 0;

  // anel de alarmes
  alarmN = 0; private alarmHead = 0;
  aT = new Float64Array(ALARM_CAP); aDx = new Float32Array(ALARM_CAP); aDy = new Float32Array(ALARM_CAP); aDz = new Float32Array(ALARM_CAP);
  aFlags = new Uint8Array(ALARM_CAP); aMode = new Uint8Array(ALARM_CAP); aMask = new Uint32Array(ALARM_CAP);
  aSlices = new Int32Array(ALARM_CAP * 4); aVers = new Int32Array(ALARM_CAP * 2); aDeltas = new Uint8Array(ALARM_CAP * 4);

  // janelas commit -> upload: [alvo(0 fil,1 cand)][causa(0 lote,1 dados)] x [n, Σms, máx ms, Σquadros, máx quadros]
  readonly win = new Float64Array(2 * 2 * 5);
  private pend = new Float64Array(2 * 3).fill(-1); // por alvo: [t do commit, quadro do commit, causa]
  readonly seq = new Float64Array(SEQ_CAP * SEQ_STRIDE).fill(NaN);
  private seqHead = 0; seqN = 0;

  // repintura do indicador (H8)
  prevUDataF: ArrayLike<any> | null = null; private prevUpT = 0;
  rpCmp = 0; rpHit = 0; rpMaxM = 0; rpMaxY = 0; rpSkipped = 0;
  readonly rpCause = new Float64Array(4); // 0 carga, 1 tick (fechamento de vela ou ao vivo), 2 só troca de lote, 3 sem etiqueta
  rpN = 0; private rpHead = 0;
  rT = new Float64Array(RP_CAP); rM = new Float32Array(RP_CAP); rY = new Float32Array(RP_CAP);
  rMode = new Uint8Array(RP_CAP); rMask = new Uint32Array(RP_CAP); rMatched = new Uint16Array(RP_CAP);

  frameNo = 0;
  private prevNow = 0;
  private t0 = 0;
  ovN = 0; ovSum = 0; ovMax = 0;

  // ---------------------------------------------------------------- contadores e eventos de pipeline
  count(id: number): void { this.counts[this.curMode * NC + id]++; this.tot[id]++; }

  private seqSet(field: number, now: number, firstNaNOfLast3: boolean) {
    const n = Math.min(this.seqN, 3);
    for (let k = n; k >= 1; k--) { // do mais antigo dos 3 últimos ao mais novo
      const idx = ((this.seqHead - k + SEQ_CAP * 2) % SEQ_CAP) * SEQ_STRIDE;
      if (isNaN(this.seq[idx + field * 2])) { this.seq[idx + field * 2] = now; this.seq[idx + field * 2 + 1] = this.frameNo; return; }
      if (!firstNaNOfLast3) return;
    }
  }
  /** ChartDataSlicer chamou setSliceOffset (início de uma troca de lote). */
  loteDisparo(now: number): void {
    const idx = this.seqHead * SEQ_STRIDE;
    for (let i = 0; i < SEQ_STRIDE; i++) this.seq[idx + i] = NaN;
    this.seq[idx] = now; this.seq[idx + 1] = this.frameNo;
    this.seqHead = (this.seqHead + 1) % SEQ_CAP; this.seqN++;
  }
  /** Layout effect da cena: dados/lote novos do filamento (alvo 0) ou da parede (alvo 1) foram commitados. */
  commit(target: 0 | 1, now: number, isLote: boolean): void {
    this.pend[target * 3] = now; this.pend[target * 3 + 1] = this.frameNo; this.pend[target * 3 + 2] = isLote ? 0 : 1;
    this.count(isLote ? (target === 0 ? SC.COMMIT_LOTE : SC.COMMIT_LOTE_PAREDE) : (target === 0 ? SC.COMMIT_DADOS_FIL : SC.COMMIT_DADOS_CAND));
    if (isLote) { this.seqSet(target === 0 ? 1 : 3, now, true); p1LoteCommit(); }
  }
  /** Efeito passivo da cena, depois dos efeitos dos filhos (FastLine / InstancedCandles): a GPU recebeu a geometria. */
  uploaded(target: 0 | 1, now: number): void {
    this.count(target === 0 ? SC.UPLOAD_FIL : SC.UPLOAD_CAND);
    const t = this.pend[target * 3];
    if (t >= 0) {
      const ms = now - t, fr = this.frameNo - this.pend[target * 3 + 1], cause = this.pend[target * 3 + 2];
      const o = (target * 2 + cause) * 5;
      this.win[o]++; this.win[o + 1] += ms; if (ms > this.win[o + 2]) this.win[o + 2] = ms;
      this.win[o + 3] += fr; if (fr > this.win[o + 4]) this.win[o + 4] = fr;
      this.pend[target * 3] = -1;
      if (cause === 0) this.seqSet(target === 0 ? 2 : 4, now, true);
    }
    if (target === 0) this.repaintCheck(now);
  }

  /** Compara o trecho visível do upload novo com o anterior, nos MESMOS carimbos de tempo (sem alocação). */
  private repaintCheck(now: number): void {
    const nw = this.uDataF, pv = this.prevUDataF, tPrev = this.prevUpT;
    this.prevUDataF = nw; this.prevUpT = now;
    if (!nw || !pv || nw === pv || nw.length < 8 || pv.length < 8) return;
    const i0 = this.uPadF, i1 = Math.min(nw.length - 4, this.uPadF + this.vis); // sem as últimas velas (ponta ao vivo)
    if (i1 - i0 < 8) { this.rpSkipped++; return; }
    const K = 24, step = Math.max(1, ((i1 - i0) / K) | 0);
    let maxM = 0, maxY = 0, refM = 0, matched = 0;
    for (let i = i0; i <= i1; i += step) {
      const a = nw[i], T = a.timestamp;
      let lo = 0, hi = pv.length;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (pv[mid].timestamp < T) lo = mid + 1; else hi = mid; }
      if (lo >= pv.length || pv[lo].timestamp !== T) continue;
      const b = pv[lo]; matched++;
      const am = a.momentum || 0, bm = b.momentum || 0;
      const dm = am > bm ? am - bm : bm - am; if (dm > maxM) maxM = dm;
      const aa = am < 0 ? -am : am; if (aa > refM) refM = aa;
      const ya = (a.trend || a.price || 0) + (a.imf || 0), yb = (b.trend || b.price || 0) + (b.imf || 0);
      const dy = ya > yb ? ya - yb : yb - ya; if (dy > maxY) maxY = dy;
    }
    if (matched < 3) { this.rpSkipped++; return; }
    const range = this.uMaxF - this.uMinF;
    const pm = refM > 0 ? (maxM / refM) * 100 : 0, py = range > 0 ? (maxY / range) * 100 : 0;
    this.rpCmp++;
    if (pm > this.rpMaxM) this.rpMaxM = pm;
    if (py > this.rpMaxY) this.rpMaxY = py;
    if (pm <= TH_RP_M && py <= TH_RP_Y) return;
    this.rpHit++;
    const mask = jank ? jank.maskSince(tPrev) : 0;
    const cause = mask & ((1 << EV.CARGA_INICIO) | (1 << EV.CARGA_FIM) | (1 << EV.MATH_CARGA)) ? 0
      : mask & (1 << EV.MATH_TICK) ? 1 : mask === 0 ? 3 : 2;
    this.rpCause[cause]++;
    const i = this.rpHead;
    this.rT[i] = now; this.rM[i] = pm; this.rY[i] = py; this.rMode[i] = this.curMode; this.rMask[i] = mask; this.rMatched[i] = matched;
    this.rpHead = (i + 1) % RP_CAP; this.rpN++;
  }

  // ---------------------------------------------------------------- um chamado por quadro (useFrame da cena)
  frame(now: number): void {
    const a = performance.now();
    const prev = this.prevNow; this.prevNow = now; this.frameNo++;
    const nav = jank ? jank.lastMode : 0;
    const m = statMode(nav, this.pos, this.autoRotateOn, this.camMoving);
    this.curMode = m;
    if (prev === 0) { this.t0 = now; this.snap(); return; }
    const dt = now - prev;
    if (dt > 5000) { this.snap(); return; }
    if (jank && now < jank.bgUntil) { this.snap(); this.overhead(a); return; } // P1b T6(d): volta do segundo plano
    this.modeFrames[m]++; this.modeTime[m] += dt;
    if (this.pendNowF) this.pendF[m]++;
    if (this.pendNowC) this.pendC[m]++;
    const uF = this.uDataF, uC = this.uDataC;
    if (!uF || !uC || uF.length < 2 || uC.length < 2) { this.snap(); this.overhead(a); return; }

    let flags = 0;
    if (this.cDataF !== uF) { flags |= F_STALE_F; this.staleF[m]++; }
    if (this.cDataC !== uC) { flags |= F_STALE_C; this.staleC[m]++; }

    // vela de referência: a que está no centro da janela visível segundo o caminho dos candles
    let ic = Math.floor(this.uPadC - this.gxC / this.stepX + this.vis / 2);
    if (ic < 0) ic = 0; else if (ic > uC.length - 1) ic = uC.length - 1;
    const T = uC[ic].timestamp;
    let lo = 0, hi = uF.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (uF[mid].timestamp < T) lo = mid + 1; else hi = mid; }
    let dx = 0, dy = 0, dz = 0;
    if (lo >= uF.length || uF[lo].timestamp !== T) { flags |= F_FORA; dx = 99; }
    else {
      const xc = (ic - this.uPadC) * this.stepX + this.gxC;
      const xf = (lo - this.uPadF) * this.stepX + this.gxF;
      dx = (xf - xc) / this.stepX;
      const d = uC[ic]; const P = d.close ?? d.price;
      const rC = this.uMaxC - this.uMinC, rF = this.uMaxF - this.uMinF, H = this.H;
      if (rC > 0 && rF > 0 && P === P) {
        const ybC = ((P - this.uMinC) / rC - 0.5) * H, ybF = ((P - this.uMinF) / rF - 0.5) * H;
        dy = (((this.yAF * ybF + this.yCF) - (this.yAC * ybC + this.yCC)) / H) * 100;
      }
    }
    if (this.zsExp > 0) dz = ((this.zKF * this.uZsF) / this.zsExp - 1) * 100;
    if (!this.drawVisible || this.drawCount < 2 || this.drawCount !== this.uCntF) flags |= F_PISCADA;

    const adx = dx < 0 ? -dx : dx, ady = dy < 0 ? -dy : dy;
    if (!(flags & F_FORA)) { if (adx > this.maxAbsDx) this.maxAbsDx = adx; if (ady > this.maxAbsDy) this.maxAbsDy = ady; }
    const bigX = adx > TH_DX && !(flags & F_FORA), bigY = ady > TH_DY;
    if (bigX || bigY || (flags & (F_PISCADA | F_FORA))) {
      if (bigX) this.alarmDx[m]++;
      if (bigY) this.alarmDy[m]++;
      if (flags & F_PISCADA) this.alarmBlink[m]++;
      if (flags & F_FORA) this.alarmOut[m]++;
      this.alarmAny[m]++;
      const mask = jank ? jank.maskSince(prev) : 0;
      if (mask === 0) this.alarmNoCause++;
      else for (let e = 0; e < NEV; e++) if (mask & (1 << e)) this.causeCounts[e]++;
      const i = this.alarmHead;
      this.aT[i] = now; this.aDx[i] = dx; this.aDy[i] = dy; this.aDz[i] = dz; this.aFlags[i] = flags; this.aMode[i] = m; this.aMask[i] = mask;
      this.aSlices[i * 4] = this.cSliceF; this.aSlices[i * 4 + 1] = this.uSliceF; this.aSlices[i * 4 + 2] = this.cSliceC; this.aSlices[i * 4 + 3] = this.uSliceC;
      this.aVers[i * 2] = this.verF; this.aVers[i * 2 + 1] = this.verC;
      const t = this.tot, l = this.lastTot;
      this.aDeltas[i * 4] = Math.min(255, t[SC.SCENE_RENDER] - l[SC.SCENE_RENDER]);
      this.aDeltas[i * 4 + 1] = Math.min(255, t[SC.WAVE_REBUILD_MAIN] - l[SC.WAVE_REBUILD_MAIN]);
      this.aDeltas[i * 4 + 2] = Math.min(255, t[SC.UPLOAD_FIL] - l[SC.UPLOAD_FIL]);
      this.aDeltas[i * 4 + 3] = Math.min(255, t[SC.UPLOAD_CAND] - l[SC.UPLOAD_CAND]);
      this.alarmHead = (i + 1) % ALARM_CAP; this.alarmN++;
    }
    this.snap();
    this.overhead(a);
  }
  private snap() { this.lastTot.set(this.tot); }
  private overhead(a: number) { const ms = performance.now() - a; this.ovN++; this.ovSum += ms; if (ms > this.ovMax) this.ovMax = ms; }

  // ---------------------------------------------------------------- consultas (fora do caminho quente)
  get alarmSize() { return this.alarmN < ALARM_CAP ? this.alarmN : ALARM_CAP; }
  alarmAt(k: number) { return (this.alarmHead - this.alarmSize + k + ALARM_CAP * 2) % ALARM_CAP; }

  /** Fração do tempo (0..1) em que há um evento (em ordem de tempo) dentro de ±halfMs: nível de acaso para uma marca aleatória. */
  private coverage(times: number[], halfMs: number, spanStart: number, spanEnd: number): number {
    const n = times.length; if (n === 0 || spanEnd <= spanStart) return 0;
    let covered = 0, curS = -Infinity, curE = -Infinity;
    for (let k = 0; k < n; k++) {
      const t = times[k];
      const s = Math.max(spanStart, t - halfMs), e = Math.min(spanEnd, t + halfMs);
      if (s > curE) { if (curE > curS) covered += curE - curS; curS = s; curE = e; } else if (e > curE) curE = e;
    }
    if (curE > curS) covered += curE - curS;
    return covered / (spanEnd - spanStart);
  }
  /** Nível de acaso para os alarmes de Δx/Δy/piscada. */
  chanceCoverage(halfMs: number, spanStart: number, spanEnd: number): number {
    const ts: number[] = []; for (let k = 0; k < this.alarmSize; k++) ts.push(this.aT[this.alarmAt(k)]);
    return this.coverage(ts, halfMs, spanStart, spanEnd);
  }
  /** P1: maior repintura (momentum % / Y %) com |t − tm| <= halfMs; NaN em nearM/nearY se não houver. Fora do caminho quente. */
  nearM = NaN; nearY = NaN;
  repaintNear(tm: number, halfMs: number): void {
    this.nearM = NaN; this.nearY = NaN;
    const n = Math.min(this.rpN, RP_CAP);
    for (let k = n - 1; k >= 0; k--) {
      const i = (this.rpHead - n + k + RP_CAP * 2) % RP_CAP, dt = this.rT[i] - tm;
      if (dt > halfMs) continue;
      if (dt < -halfMs) break;
      if (!(this.nearM >= this.rM[i])) this.nearM = this.rM[i];
      if (!(this.nearY >= this.rY[i])) this.nearY = this.rY[i];
    }
  }
  /** Nível de acaso para as repinturas do indicador. */
  chanceCoverageRepaint(halfMs: number, spanStart: number, spanEnd: number): number {
    const ts: number[] = []; const n = Math.min(this.rpN, RP_CAP);
    for (let k = 0; k < n; k++) ts.push(this.rT[(this.rpHead - n + k + RP_CAP * 2) % RP_CAP]);
    return this.coverage(ts, halfMs, spanStart, spanEnd);
  }

  /** Topo do "Copiar resumo": alarmes de DESSINCRONIA por tipo (total e por modo) e os últimos, em poucas linhas. */
  summaryTop(t0Abs: number): string[] {
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sum = (a: Float64Array) => a.reduce((x, y) => x + y, 0);
    const alarms = sum(this.alarmAny);
    L.push(`=== DESSINCRONIA: ${alarms} alarmes | Δx ${sum(this.alarmDx)} / Δy ${sum(this.alarmDy)} / piscada ${sum(this.alarmBlink)} / fora do lote ${sum(this.alarmOut)} | |Δx| máx ${f(this.maxAbsDx, 2)} vela | |Δy| máx ${f(this.maxAbsDy, 2)}% ===`);
    for (let m = 0; m < NSM; m++) if (this.alarmAny[m] > 0) L.push(`${SMODE_NAMES[m]}: ${this.alarmAny[m]} (Δx ${this.alarmDx[m]} / Δy ${this.alarmDy[m]} / piscada ${this.alarmBlink[m]} / fora ${this.alarmOut[m]})`);
    const N = 5;
    for (let k = Math.max(0, this.alarmSize - N); k < this.alarmSize; k++) {
      const i = this.alarmAt(k), fl = this.aFlags[i];
      const tipo = [fl & F_STALE_F ? 'fil velho' : '', fl & F_STALE_C ? 'cand velhos' : '', fl & F_PISCADA ? 'PISCADA' : '', fl & F_FORA ? 'FORA' : ''].filter(Boolean).join(',') || 'lote ok';
      L.push(`t=${rel(this.aT[i])} ${SMODE_NAMES[this.aMode[i]]} Δx=${f(this.aDx[i], 2)} Δy=${f(this.aDy[i], 2)}% [${tipo}] lote fil ${this.aSlices[i * 4]}/${this.aSlices[i * 4 + 1]} parede ${this.aSlices[i * 4 + 2]}/${this.aSlices[i * 4 + 3]} [${jank ? jank.labelsOf(this.aMask[i]).slice(0, 90) : ''}]`);
    }
    return L;
  }

  /** Seção "DESSINCRONIA" do "Copiar log". */
  report(t0Abs: number, cleanMode: boolean): string[] {
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sess = this.prevNow > 0 ? this.prevNow - this.t0 : 0;
    const frames = this.modeFrames.reduce((a, b) => a + b, 0);
    const alarms = this.alarmAny.reduce((a, b) => a + b, 0);
    const lng = jank ? jank.ring25.n : 0;
    L.push('=== DESSINCRONIA (filamento x candles) ===');
    L.push(`modo do log: ${cleanMode ? 'LIMPO (clean=1: sem atualização de DOM por setInterval, sem painel do profiler/r3f-perf)' : 'normal'}`);
    L.push('LEGENDA: Δx = posição da mesma vela pelo filamento menos pelos candles (em larguras de vela); Δy = o mesmo para o preço, em % da altura; Δz = escala Z aplicada contra a pretendida, em %. "Velho" = o lote que o useFrame USA para posicionar/escalar não é o que está na GPU. "Commit pendente" = o React já commitou lote novo que ainda não está na GPU. Alarme: |Δx|>0,25 | |Δy|>0,5% | piscada | vela fora do lote do filamento.');
    L.push(`sessão sondada: ${f(sess / 1000, 0)}s | quadros: ${frames} | alarmes: ${alarms} (${f(alarms / Math.max(1e-9, sess / 60000), 1)}/min) | quadros longos >=25ms (gravador): ${lng} (${f(lng / Math.max(1e-9, sess / 60000), 1)}/min) | |Δx| máx ${f(this.maxAbsDx, 2)} vela | |Δy| máx ${f(this.maxAbsDy, 2)}%`);
    L.push(`overhead da sonda: média ${f((this.ovSum / Math.max(1, this.ovN)) * 1000, 1)} µs/quadro, máx ${f(this.ovMax, 3)} ms`);

    // janelas commit -> upload
    L.push('');
    L.push('--- janela commit do React -> envio à GPU (efeito passivo) ---');
    const names = ['filamento', 'candles'], causes = ['troca de lote', 'dados novos (tick/carga)'];
    for (let tg = 0; tg < 2; tg++) for (let c = 0; c < 2; c++) {
      const o = (tg * 2 + c) * 5, n = this.win[o];
      L.push(`${names[tg]} / ${causes[c]}: ${n} ocorrências | ms: média ${f(this.win[o + 1] / Math.max(1, n), 1)} máx ${f(this.win[o + 2], 1)} | quadros de rAF no meio: média ${f(this.win[o + 3] / Math.max(1, n), 2)} máx ${this.win[o + 4]}`);
    }

    // repintura do indicador (H8)
    L.push('');
    {
      const mn = Math.max(1e-9, sess / 60000);
      L.push(`--- repintura do indicador (o filamento muda nos pontos que já existiam; os candles não mudam) ---`);
      L.push(`uploads comparados: ${this.rpCmp} (ignorados por trecho curto/sem pontos em comum: ${this.rpSkipped}) | com repintura (momentum >${TH_RP_M}% ou Y >${String(TH_RP_Y).replace('.', ',')}%): ${this.rpHit} (${this.rpCmp ? f((this.rpHit / this.rpCmp) * 100, 0) : '—'}%, ${f(this.rpHit / mn, 1)}/min) | maior: momentum ${f(this.rpMaxM, 1)}% | Y ${f(this.rpMaxY, 2)}% da altura do lote`);
      L.push(`causa (etiquetas do gravador entre dois uploads): carga ${this.rpCause[0]} | tick/fechamento de vela ${this.rpCause[1]} | só troca de lote ${this.rpCause[2]} | sem etiqueta ${this.rpCause[3]}`);
      const nR = Math.min(this.rpN, RP_CAP), shown = Math.min(nR, 12);
      for (let k = shown; k >= 1; k--) {
        const i = (this.rpHead - k + RP_CAP * 2) % RP_CAP;
        L.push(`t=${rel(this.rT[i])} ${SMODE_NAMES[this.rMode[i]]} momentum ${f(this.rM[i], 1)}% | Y ${f(this.rY[i], 2)}% | pontos comparados ${this.rMatched[i]} [${jank ? jank.labelsOf(this.rMask[i]) : ''}]`);
      }
    }

    // por modo
    L.push('');
    L.push('--- por modo: quadros, alarmes/min, quadros com dado "velho" ---');
    L.push('modo | tempo | quadros | alarmes/min (Δx / Δy / piscada / fora) | quadros velhos (lote USADO NO DESENHO ≠ GPU): filamento / candles | commit pendente (React à frente da GPU; o desenho segura o lote antigo): filamento / candles');
    for (let m = 0; m < NSM; m++) {
      if (this.modeFrames[m] === 0) { L.push(`${SMODE_NAMES[m]} | sem dados`); continue; }
      const mn = Math.max(1e-9, this.modeTime[m] / 60000);
      L.push(`${SMODE_NAMES[m]} | ${f(this.modeTime[m] / 1000, 0)}s | ${this.modeFrames[m]} | ${f(this.alarmAny[m] / mn, 1)} (${f(this.alarmDx[m] / mn, 1)} / ${f(this.alarmDy[m] / mn, 1)} / ${f(this.alarmBlink[m] / mn, 1)} / ${f(this.alarmOut[m] / mn, 1)}) | ${this.staleF[m]} (${f((this.staleF[m] / this.modeFrames[m]) * 100, 2)}%) / ${this.staleC[m]} (${f((this.staleC[m] / this.modeFrames[m]) * 100, 2)}%) | ${this.pendF[m]} / ${this.pendC[m]}`);
    }

    // causas dos alarmes
    L.push('');
    L.push(`--- causas dos quadros com alarme (${alarms} alarmes; % que têm a etiqueta) ---`);
    const rows: [string, number][] = [];
    for (let e = 0; e < NEV; e++) if (this.causeCounts[e] > 0) rows.push([EV_NAMES[e], this.causeCounts[e]]);
    if (this.alarmNoCause) rows.push(['sem etiqueta', this.alarmNoCause]);
    rows.sort((a, b) => b[1] - a[1]);
    L.push(rows.map(([n, c]) => `${n}: ${alarms ? ((c / alarms) * 100).toFixed(0) : '—'}% (${c})`).join(' | ') || '—');

    // últimos alarmes
    L.push('');
    const N = 20;
    L.push(`--- últimos ${Math.min(N, this.alarmSize)} quadros com alarme ---`);
    for (let k = Math.max(0, this.alarmSize - N); k < this.alarmSize; k++) {
      const i = this.alarmAt(k), fl = this.aFlags[i];
      const tipo = [fl & F_STALE_F ? 'filamento velho' : '', fl & F_STALE_C ? 'candles velhos' : '', fl & F_PISCADA ? 'PISCADA' : '', fl & F_FORA ? 'FORA DO LOTE' : ''].filter(Boolean).join(',') || 'lote ok';
      const lab = jank ? jank.labelsOf(this.aMask[i]) : '';
      L.push(`t=${rel(this.aT[i])} ${SMODE_NAMES[this.aMode[i]]} Δx=${f(this.aDx[i], 2)} Δy=${f(this.aDy[i], 2)}% Δz=${f(this.aDz[i], 1)}% [${tipo}] lote fil commit/gpu=${this.aSlices[i * 4]}/${this.aSlices[i * 4 + 1]} parede commit/gpu=${this.aSlices[i * 4 + 2]}/${this.aSlices[i * 4 + 3]} versões buf fil/cand=${this.aVers[i * 2]}/${this.aVers[i * 2 + 1]} Δrenders cena/rebuild/upFil/upCand=${this.aDeltas[i * 4]}/${this.aDeltas[i * 4 + 1]}/${this.aDeltas[i * 4 + 2]}/${this.aDeltas[i * 4 + 3]} [${lab}]`);
    }

    // sequência das últimas trocas de lote
    L.push('');
    const nS = Math.min(this.seqN, 12);
    L.push(`--- sequência das últimas ${nS} trocas de lote (ms e quadros de rAF desde o disparo) ---`);
    L.push('disparo -> commit | -> upload filamento | -> commit parede | -> upload candles');
    for (let k = nS; k >= 1; k--) {
      const b = ((this.seqHead - k + SEQ_CAP * 2) % SEQ_CAP) * SEQ_STRIDE;
      const d = (fld: number) => (isNaN(this.seq[b + fld * 2]) ? '—' : `+${f(this.seq[b + fld * 2] - this.seq[b], 0)}ms/${this.seq[b + fld * 2 + 1] - this.seq[b + 1]}q`);
      L.push(`t=${rel(this.seq[b])} commit ${d(1)} | upload fil ${d(2)} | commit parede ${d(3)} | upload cand ${d(4)}`);
    }

    // contadores por modo
    L.push('');
    L.push('--- contadores por segundo, por modo (S5) ---');
    L.push('contador | ' + SMODE_NAMES.join(' | '));
    for (let id = 0; id < NC; id++) {
      const cells: string[] = [];
      for (let m = 0; m < NSM; m++) cells.push(this.modeTime[m] > 0 ? f(this.counts[m * NC + id] / (this.modeTime[m] / 1000), 2) : '—');
      L.push(`${SC_NAMES[id]} | ${cells.join(' | ')}`);
    }
    if (jank) {
      const ticks = jank.evCount[EV.MATH_TICK], lotes = jank.evCount[EV.LOTE];
      const r = (id: number, den: number) => (den > 0 ? f(this.tot[id] / den, 2) : '—');
      L.push(`por tick (${ticks} ticks): renders da cena ${r(SC.SCENE_RENDER, ticks)} | reconstruções do lote principal ${r(SC.WAVE_REBUILD_MAIN, ticks)} | da parede ${r(SC.WAVE_REBUILD_WALL, ticks)} | uploads filamento ${r(SC.UPLOAD_FIL, ticks)} | uploads candles ${r(SC.UPLOAD_CAND, ticks)} (totais da sessão, inclui os causados por lote)`);
      L.push(`por troca de lote (${lotes}): renders da cena ${r(SC.SCENE_RENDER, lotes)} | uploads filamento ${r(SC.UPLOAD_FIL, lotes)} | uploads candles ${r(SC.UPLOAD_CAND, lotes)} (totais da sessão)`);
    }

    // marcas do filamento
    L.push('');
    if (jank) {
      const n = Math.min(jank.fmN, jank.fmCap);
      const spanS = this.t0, spanE = this.prevNow;
      const chance100 = this.chanceCoverage(100, spanS, spanE), chance400 = this.chanceCoverage(400, spanS, spanE);
      let h100 = 0, h400 = 0; const lines: string[] = [];
      for (let k = 0; k < n; k++) {
        const i = (jank.fmN <= jank.fmCap ? k : (jank.fmN + k) % jank.fmCap);
        const tm = jank.fmT[i]; let best = Infinity, bi = -1;
        for (let q = 0; q < this.alarmSize; q++) { const j = this.alarmAt(q); const dd = Math.abs(this.aT[j] - tm); if (dd < best) { best = dd; bi = j; } }
        if (best <= 100) h100++; if (best <= 400) h400++;
        lines.push(`t=${rel(tm)} ${MODE_NAMES[jank.fmMode[i]]} pos=${f(jank.fmPos[i], 0)} → ${bi >= 0 ? `alarme mais próximo a ${f(best, 0)}ms (Δx=${f(this.aDx[bi], 2)} Δy=${f(this.aDy[bi], 2)}%)` : 'nenhum alarme'}`);
      }
      L.push(`--- marcas de filamento: ${n} | alarme em ±100ms: ${h100} (${n ? ((h100 / n) * 100).toFixed(0) : '—'}%) contra acaso ${f(chance100 * 100, 0)}% | em ±400ms: ${h400} (${n ? ((h400 / n) * 100).toFixed(0) : '—'}%) contra acaso ${f(chance400 * 100, 0)}% ---`);
      // repintura do indicador perto das marcas (a outra explicação possível para "o filamento voltou a um estado anterior")
      let r400 = 0, r1000 = 0; const nR = Math.min(this.rpN, RP_CAP);
      for (let k = 0; k < n; k++) {
        const i = (jank.fmN <= jank.fmCap ? k : (jank.fmN + k) % jank.fmCap), tm = jank.fmT[i]; let best = Infinity;
        for (let q = 0; q < nR; q++) { const dd = Math.abs(this.rT[(this.rpHead - nR + q + RP_CAP * 2) % RP_CAP] - tm); if (dd < best) best = dd; }
        if (best <= 400) r400++; if (best <= 1000) r1000++;
      }
      L.push(`--- marcas de filamento perto de repintura do indicador (${this.rpHit} repinturas): em ±400ms: ${r400} (${n ? ((r400 / n) * 100).toFixed(0) : '—'}%) contra acaso ${f(this.chanceCoverageRepaint(400, spanS, spanE) * 100, 0)}% | em ±1000ms: ${r1000} (${n ? ((r1000 / n) * 100).toFixed(0) : '—'}%) contra acaso ${f(this.chanceCoverageRepaint(1000, spanS, spanE) * 100, 0)}% ---`);
      for (const ln of lines.slice(-30)) L.push(ln);
    }
    return L;
  }
}

export const sync: SyncProbe | null = DBG_ENABLED ? new SyncProbe() : null;
if (p1 && sync) p1.repaint = sync; // P1: as marcas consultam as repinturas do indicador

/** ChartDataSlicer chamou setSliceOffset: início de uma troca de lote (no-op sem a flag). */
export function syncLoteDisparo(): void {
  if (!DBG_ENABLED) return;
  markEvent(EV.LOTE_DISPARO);
  p1LoteDisparo();
  sync!.loteDisparo(performance.now());
}

/** Contador barato para o corpo dos componentes/hooks (no-op sem a flag). */
export function syncCount(id: number): void {
  if (!DBG_ENABLED) return;
  sync!.count(id);
}
